import { useListProducts, useUnlockGift, useUnlockProduct, useUnlockResale } from "@/store/wallet/wallet.service";
import { WalletStore } from "@/store/wallet/wallet.store";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { toast } from "sonner";
import { useProfileLanguageSync } from "@/utils/useProfileLanguageSync";

type Payload = {
    a: string;
    id: string;
}

const Home = () => {
    const navigate = useNavigate();
    const [productId, setProductId] = useState<string | null>(null);
    const [payload, setPayload] = useState<Payload | null>(null);
    const [loading, setLoading] = useState(true);
    const { refetch: refetchProducts } = useListProducts();

    const setProducts = WalletStore((s) => s.setProducts);

    const session = sessionStorage.getItem('_session');

    const { t } = useTranslation();
    const { LanguageUpdateModal } = useProfileLanguageSync();

    useEffect(() => {
        if (session) {
            setPayload(JSON.parse(session) as Payload);
            sessionStorage.removeItem('_session');
        }
    }, [session]);

    const { mutate: unlockProduct } = useUnlockProduct({
        onSuccess: async () => {
            const { data } = await refetchProducts();
            setProducts(data!);
            setLoading(false);
            navigate(productId!, { state: { unlocked: true, type: 'unlock' } });
        },
        onError: (message) => {
            setLoading(false);
            toast.error(message)
        },
    });

    const { mutate: unlockGift } = useUnlockGift({
        onSuccess: async () => {
            const { data } = await refetchProducts();
            setProducts(data!);
            setLoading(false);
            navigate(productId!, { state: { unlocked: true, type: 'gift' } });
        },
        onError: (message) => {
            setLoading(false);
            toast.error(message);
        },
    });

    const { mutate: unlockResale } = useUnlockResale({
        onSuccess: async () => {
            const { data } = await refetchProducts();
            setProducts(data!);
            setLoading(false);
            navigate(productId!, { state: { unlocked: true, type: 'resale' } });
        },
        onError: (message) => {
            setLoading(false);
            toast.error(message);
        },
    });

    useEffect(() => {
        if (payload) {
            if (payload.a === 'unlock') {
                setProductId(payload.id);
                unlockProduct(payload.id);
            } else if (payload.a === 'transfer') {
                setProductId(payload.id);
                const products = WalletStore.getState().products;
                const product = products.find(p => p._id === payload.id);
                if (product) {
                    if (product.gift && !product.gift.seen) {
                        unlockGift(product.gift._id);
                    } else if (product.resale && !product.resale.seen) {
                        unlockResale(product.resale._id);
                    } else {
                        setLoading(false);
                        toast.info(t('unlock.toast.unlocked'));
                    }
                } else {
                    setLoading(false);
                    toast.error(t('unlock.toast.notFound'));
                }
            }
        } else {
            setLoading(false);
        }
    }, [payload, unlockGift, unlockProduct, unlockResale])

    return loading
        ? <div className="w-full h-full flex justify-center items-center">
            <ClipLoader />
        </div>
        : (
            <>
                <Outlet />
                <LanguageUpdateModal />
            </>
        )
}

export default Home;
